import React, { useMemo } from 'react';

const ConfidenceSankey = ({ data }) => {
  const sankeyData = useMemo(() => {
    // Group by confidence levels
    const highConfidence = data.filter(item => item.confidenceScore >= 85);
    const mediumConfidence = data.filter(item => item.confidenceScore >= 70 && item.confidenceScore < 85);
    const lowConfidence = data.filter(item => item.confidenceScore >= 50 && item.confidenceScore < 70);
    const notFound = data.filter(item => item.confidenceScore < 50);

    // Group by entity types
    const entityTypes = {};
    data.forEach(item => {
      const type = item.entityType || 'UNKNOWN';
      entityTypes[type] = (entityTypes[type] || 0) + 1;
    });

    return {
      confidenceLevels: {
        high: { count: highConfidence.length, color: '#10b981' },
        medium: { count: mediumConfidence.length, color: '#eab308' },
        low: { count: lowConfidence.length, color: '#f97316' },
        notFound: { count: notFound.length, color: '#ef4444' }
      },
      entityTypes,
      total: data.length
    };
  }, [data]);

  const maxCount = Math.max(
    sankeyData.confidenceLevels.high.count,
    sankeyData.confidenceLevels.medium.count,
    sankeyData.confidenceLevels.low.count,
    sankeyData.confidenceLevels.notFound.count
  );

  return (
    <div className="bg-dark-card rounded-lg border border-dark-border p-6">
      <h3 className="text-xl font-bold text-cyan-accent mb-6">Confidence Score Flow</h3>
      
      <div className="flex items-center gap-8">
        {/* Left: Changes */}
        <div className="flex-shrink-0 w-32">
          <div className="text-center">
            <div className="text-sm text-gray-400 mb-2">Changes</div>
            <div className="bg-cyan-accent/20 border border-cyan-accent/40 rounded-lg p-4"
                 style={{ boxShadow: '0 0 20px rgba(0, 229, 255, 0.2)' }}>
              <div className="text-3xl font-bold text-cyan-accent">{sankeyData.total}</div>
              <div className="text-xs text-gray-400 mt-1">Total</div>
            </div>
          </div>
        </div>

        {/* Middle: Confidence Levels with Flows */}
        <div className="flex-1 relative" style={{ minHeight: '400px' }}>
          <div className="absolute inset-0 flex flex-col justify-around">
            {/* High Confidence */}
            <div className="flex items-center gap-4">
              <div className="flex-1 relative h-16">
                <svg className="absolute inset-0 w-full h-full" style={{ overflow: 'visible' }}>
                  <defs>
                    <linearGradient id="gradHigh" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" style={{ stopColor: '#00e5ff', stopOpacity: 0.3 }} />
                      <stop offset="100%" style={{ stopColor: '#10b981', stopOpacity: 0.6 }} />
                    </linearGradient>
                  </defs>
                  <path
                    d={`M 0,${32} C 100,${32} 100,${32} 200,${32}`}
                    fill="none"
                    stroke="url(#gradHigh)"
                    strokeWidth={Math.max(2, (sankeyData.confidenceLevels.high.count / maxCount) * 60)}
                    opacity="0.8"
                  />
                </svg>
              </div>
              <div className="flex-shrink-0 w-48 bg-dark-border/20 border border-lime-accent/40 rounded-lg p-3"
                   style={{ boxShadow: '0 0 15px rgba(16, 185, 129, 0.2)' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-lime-accent">High (85-100)</div>
                    <div className="text-xs text-gray-500">Confident Match</div>
                  </div>
                  <div className="text-2xl font-bold text-lime-accent">
                    {sankeyData.confidenceLevels.high.count}
                  </div>
                </div>
              </div>
            </div>

            {/* Medium Confidence */}
            <div className="flex items-center gap-4">
              <div className="flex-1 relative h-16">
                <svg className="absolute inset-0 w-full h-full" style={{ overflow: 'visible' }}>
                  <defs>
                    <linearGradient id="gradMedium" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" style={{ stopColor: '#00e5ff', stopOpacity: 0.3 }} />
                      <stop offset="100%" style={{ stopColor: '#eab308', stopOpacity: 0.6 }} />
                    </linearGradient>
                  </defs>
                  <path
                    d={`M 0,${32} C 100,${32} 100,${32} 200,${32}`}
                    fill="none"
                    stroke="url(#gradMedium)"
                    strokeWidth={Math.max(2, (sankeyData.confidenceLevels.medium.count / maxCount) * 60)}
                    opacity="0.8"
                  />
                </svg>
              </div>
              <div className="flex-shrink-0 w-48 bg-dark-border/20 border border-yellow-accent/40 rounded-lg p-3"
                   style={{ boxShadow: '0 0 15px rgba(234, 179, 8, 0.2)' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-yellow-accent">Medium (70-84)</div>
                    <div className="text-xs text-gray-500">Likely Match</div>
                  </div>
                  <div className="text-2xl font-bold text-yellow-accent">
                    {sankeyData.confidenceLevels.medium.count}
                  </div>
                </div>
              </div>
            </div>

            {/* Low Confidence */}
            <div className="flex items-center gap-4">
              <div className="flex-1 relative h-16">
                <svg className="absolute inset-0 w-full h-full" style={{ overflow: 'visible' }}>
                  <defs>
                    <linearGradient id="gradLow" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" style={{ stopColor: '#00e5ff', stopOpacity: 0.3 }} />
                      <stop offset="100%" style={{ stopColor: '#f97316', stopOpacity: 0.6 }} />
                    </linearGradient>
                  </defs>
                  <path
                    d={`M 0,${32} C 100,${32} 100,${32} 200,${32}`}
                    fill="none"
                    stroke="url(#gradLow)"
                    strokeWidth={Math.max(2, (sankeyData.confidenceLevels.low.count / maxCount) * 60)}
                    opacity="0.8"
                  />
                </svg>
              </div>
              <div className="flex-shrink-0 w-48 bg-dark-border/20 border border-orange-accent/40 rounded-lg p-3"
                   style={{ boxShadow: '0 0 15px rgba(249, 115, 22, 0.2)' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-orange-accent">Low (50-69)</div>
                    <div className="text-xs text-gray-500">Uncertain</div>
                  </div>
                  <div className="text-2xl font-bold text-orange-accent">
                    {sankeyData.confidenceLevels.low.count}
                  </div>
                </div>
              </div>
            </div>

            {/* Not Found */}
            <div className="flex items-center gap-4">
              <div className="flex-1 relative h-16">
                <svg className="absolute inset-0 w-full h-full" style={{ overflow: 'visible' }}>
                  <defs>
                    <linearGradient id="gradNotFound" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" style={{ stopColor: '#00e5ff', stopOpacity: 0.3 }} />
                      <stop offset="100%" style={{ stopColor: '#ef4444', stopOpacity: 0.6 }} />
                    </linearGradient>
                  </defs>
                  <path
                    d={`M 0,${32} C 100,${32} 100,${32} 200,${32}`}
                    fill="none"
                    stroke="url(#gradNotFound)"
                    strokeWidth={Math.max(2, (sankeyData.confidenceLevels.notFound.count / maxCount) * 60)}
                    opacity="0.8"
                  />
                </svg>
              </div>
              <div className="flex-shrink-0 w-48 bg-dark-border/20 border border-red-500/40 rounded-lg p-3"
                   style={{ boxShadow: '0 0 15px rgba(239, 68, 68, 0.2)' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-red-500">Not Found (0-49)</div>
                    <div className="text-xs text-gray-500">No Match</div>
                  </div>
                  <div className="text-2xl font-bold text-red-500">
                    {sankeyData.confidenceLevels.notFound.count}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Entity Types */}
        <div className="flex-shrink-0 w-48">
          <div className="text-center mb-4">
            <div className="text-sm text-gray-400">Entity Types</div>
          </div>
          <div className="space-y-2">
            {Object.entries(sankeyData.entityTypes)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 6)
              .map(([type, count], idx) => (
                <div key={type} 
                     className="bg-dark-border/20 border border-purple-accent/30 rounded p-2"
                     style={{ boxShadow: '0 0 10px rgba(178, 75, 243, 0.1)' }}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-400 truncate" title={type}>
                      {type.length > 15 ? type.substring(0, 12) + '...' : type}
                    </span>
                    <span className="text-purple-accent font-bold">{count}</span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-6 pt-4 border-t border-dark-border/50 flex items-center justify-center gap-6 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-lime-accent"></div>
          <span className="text-gray-400">High Confidence</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-yellow-accent"></div>
          <span className="text-gray-400">Medium</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-orange-accent"></div>
          <span className="text-gray-400">Low</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <span className="text-gray-400">Not Found</span>
        </div>
      </div>
    </div>
  );
};

export default ConfidenceSankey;
